/**
 * Écran de connexion / configuration initiale de la pointeuse
 */
import { Logo } from "@/components/timeclock/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { loginPointeuse } from "@/services/auth.service";
import {
  setCompanyId,
  setCompanyInformation,
  setPointeuseToken,
} from "@/services/config.services";
import { Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export const LoginConfig = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await loginPointeuse(login, password);

      setPointeuseToken(response.token);
      setCompanyId(response.company.id);
      setCompanyInformation(response.company);

      navigate("/home");
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-50">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md shrink-0 space-y-10">
          <div className="flex items-center justify-center">
            <Logo size="hero" />
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-xl sm:p-10">
            <div className="mb-8">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                Connexion
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Connectez la pointeuse avec les identifiants fournis par votre
                entreprise.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <label
                  htmlFor="login"
                  className="text-sm font-semibold text-gray-900"
                >
                  Identifiant
                </label>
                <div className="relative">
                  <Mail
                    className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <Input
                    id="login"
                    type="text"
                    placeholder="Identifiant de la pointeuse"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    required
                    autoComplete="username"
                    className="h-11 border-gray-200 bg-gray-50 pl-10 text-gray-900 placeholder:text-muted-foreground focus-visible:bg-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-semibold text-gray-900"
                >
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="h-11 border-gray-200 bg-gray-50 pl-10 text-gray-900 placeholder:text-muted-foreground focus-visible:bg-white"
                  />
                </div>
              </div>

              {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                className="h-11 w-full font-semibold shadow-sm transition-shadow hover:shadow-md"
                disabled={loading}
              >
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
